import { moveFocus, moveNextGenerator, movePreviousGenerator } from '@ast/magma/utils/focus';

/**
 * Tries to focus the previous element relative to currently selected.
 *
 * Only focusable elements will be processed (i.e. `tabindex>=0`,
 * `aria-disabled="false"`, etc.).
 *
 * See {@link movePreviousGenerator} for details.
 *
 * @param current Currently selected element.
 * @returns Newly focused element or `null` if no element can be focused.
 */
export function tryFocusPrevElement(current: Element | null) {
  const element = movePreviousGenerator(current, false);
  return moveFocus(element);
}

/**
 * Tries to focus the next element relative to currently selected.
 *
 * Only focusable elements will be processed (i.e. `tabindex>=0`,
 * `aria-disabled="false"`, etc.).
 *
 * See {@link moveNextGenerator} for details.
 *
 * @param current Currently selected element.
 * @returns Newly focused element or `null` if no element can be focused.
 */
export function tryFocusNextElement(current: Element | null) {
  const element = moveNextGenerator(current, false);
  return moveFocus(element);
}

/**
 * Tries to focus the first element inside the specified container.
 *
 * Only focusable elements will be processed (i.e. `tabindex>=0`,
 * `aria-disabled="false"`, etc.).
 *
 * See {@link moveNextGenerator} for details.
 *
 * @param container Container element.
 * @returns Newly focused element or `null` if no element can be focused.
 */
export function tryFocusFirstElement(container: ParentNode | null) {
  const current = container ? container.firstElementChild : null;
  const element = moveNextGenerator(current, true);
  return moveFocus(element);
}

/**
 * Tries to focus the last element inside the specified container.
 *
 * Only focusable elements will be processed (i.e. `tabindex>=0`,
 * `aria-disabled="false"`, etc.).
 *
 * See {@link movePreviousGenerator} for details.
 *
 * @param container Container element.
 * @returns Newly focused element or `null` if no element can be focused.
 */
export function tryFocusLastElement(container: ParentNode | null) {
  const current = container ? container.lastElementChild : null;
  const element = movePreviousGenerator(current, true);
  return moveFocus(element);
}
